/** Browser speechSynthesis announcer for Astra Darts v2. */

const STORAGE_KEY = 'astra_announcer_v2';

const DEFAULTS = {
  muted: false,
  voiceURI: '',
  rate: 0.95,
  pitch: 1,
  volume: 1,
  soundCues: true,
};

export function loadAnnouncerPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveAnnouncerPrefs(prefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...DEFAULTS, ...prefs }));
}

export function listVoices() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];
  return window.speechSynthesis.getVoices() || [];
}

/** Prefer Australian / NZ / GB English voices when Auto is selected. */
export function pickDefaultVoice(voices) {
  const list = voices || listVoices();
  const rank = (v) => {
    const lang = (v.lang || '').toLowerCase();
    const name = (v.name || '').toLowerCase();
    if (lang === 'en-au' || /australia|karen|lee|catherine|james|gordon/.test(name)) return 100;
    if (lang === 'en-nz') return 90;
    if (lang === 'en-gb' || /british|daniel|serena|martha|arthur/.test(name)) return 80;
    if (/^en/.test(lang)) return 40;
    return 0;
  };
  return [...list].sort((a, b) => rank(b) - rank(a))[0] || null;
}

export function playCue(kind, prefsOverride) {
  try {
    const prefs = { ...loadAnnouncerPrefs(), ...(prefsOverride || {}) };
    if (prefs.muted || prefs.soundCues === false) return;
    if (typeof window === 'undefined' || (!window.AudioContext && !window.webkitAudioContext)) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = playCue._ctx || (playCue._ctx = new Ctx());
    const now = ctx.currentTime;
    const beep = (freq, t0, dur, gain = 0.08) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(gain, t0);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + dur);
    };
    if (kind === 'bust') {
      beep(180, now, 0.18, 0.1);
      beep(140, now + 0.16, 0.22, 0.1);
    } else if (kind === 'win' || kind === 'shanghai') {
      beep(523, now, 0.12, 0.09);
      beep(659, now + 0.12, 0.12, 0.09);
      beep(784, now + 0.24, 0.2, 0.1);
    } else if (kind === 'ton' || kind === 'oneeighty') {
      beep(440, now, 0.1, 0.08);
      beep(554, now + 0.1, 0.14, 0.09);
    } else if (kind === 'high') {
      beep(392, now, 0.08, 0.07);
      beep(523, now + 0.09, 0.12, 0.08);
    } else if (kind === 'golf') {
      beep(349, now, 0.1, 0.07);
      beep(440, now + 0.1, 0.14, 0.08);
    } else if (kind === 'tick') {
      beep(660, now, 0.05, 0.04);
    }
  } catch {
    /* ignore */
  }
}

export function cancelSpeech() {
  try {
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

export function speak(text, prefsOverride) {
  try {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    const prefs = { ...loadAnnouncerPrefs(), ...(prefsOverride || {}) };
    if (prefs.muted) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.rate = Number(prefs.rate) || 0.95;
    u.pitch = Number(prefs.pitch) || 1;
    u.volume = Math.max(0, Math.min(1, Number(prefs.volume) ?? 1));
    u.lang = 'en-AU';
    const voices = listVoices();
    let pick = null;
    if (prefs.voiceURI) pick = voices.find((v) => v.voiceURI === prefs.voiceURI);
    if (!pick) pick = pickDefaultVoice(voices);
    if (pick) u.voice = pick;
    window.speechSynthesis.speak(u);
  } catch {
    /* ignore */
  }
}

/** Queue several lines with short gaps (for Test-all). */
export function speakQueue(lines, prefsOverride, gapMs = 900) {
  const list = (lines || []).filter(Boolean);
  if (!list.length) return;
  let i = 0;
  const next = () => {
    if (i >= list.length) return;
    speak(list[i], prefsOverride);
    i += 1;
    if (i < list.length) setTimeout(next, gapMs);
  };
  next();
}

const ONE_EIGHTY_LINES = [
  'One hundred and eighty!',
  'Maximum!',
  'One hundred and eighty! Absolute pearler!',
  'One hundred and eighty! You little beauty!',
  'Maximum one-eighty!',
];

const HIGH_SCORE_LINES = {
  177: 'One hundred and seventy-seven',
  174: 'One hundred and seventy-four',
  171: 'One hundred and seventy-one',
  167: 'One hundred and sixty-seven',
  164: 'One hundred and sixty-four',
  161: 'One hundred and sixty-one',
  160: 'One hundred and sixty',
  140: 'One hundred and forty',
  120: 'One hundred and twenty',
  100: 'One hundred',
};

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function formatVisitCall(thrown) {
  const n = Number(thrown) || 0;
  if (n === 180) return pick(ONE_EIGHTY_LINES);
  if (HIGH_SCORE_LINES[n]) return HIGH_SCORE_LINES[n];
  if (n >= 100) return `${n}`;
  if (n >= 60) return String(n);
  return String(n);
}

function remainingPhrase(remaining) {
  const r = Number(remaining);
  if (!Number.isFinite(r)) return '';
  if (r === 0) return '';
  const variants = [
    `requires ${r}`,
    `left on ${r}`,
    `${r} remaining`,
  ];
  if (r <= 170 && r >= 2) variants.push(`looking at ${r}`);
  return pick(variants);
}

export function announce501Visit({ player, thrown, remaining, bust, win, nextPlayer, checkoutHint, darts }) {
  if (win) {
    playCue('win');
    speak(pick([
      `Game shot, and the match! ${player} checks out ${thrown}!`,
      `Match shot! ${player} for ${thrown}!`,
      `That's the game! ${player} with a ${thrown} checkout!`,
    ]));
    return;
  }
  if (bust) {
    playCue('bust');
    speak(`Bust. ${player}. ${nextPlayer ? `Up next, ${nextPlayer}.` : ''}`);
    return;
  }
  const n = Number(thrown) || 0;
  if (n === 180) playCue('oneeighty');
  else if (n >= 140) playCue('high');
  else if (n >= 100) playCue('ton');

  let visit = formatVisitCall(thrown);
  if (Array.isArray(darts) && darts.filter(Boolean).length >= 2 && n > 0 && n < 100) {
    const bits = darts.filter(Boolean).map((d) => d.label || d).join(', ');
    visit = `${visit}. ${bits}`;
  }
  const rem = remainingPhrase(remaining);
  let line = rem ? `${visit}. ${player} ${rem}.` : `${visit}. ${player}.`;
  if (checkoutHint) {
    line += ` ${pick([`Checkout ${checkoutHint}`, `Route ${checkoutHint}`, `Looking at ${checkoutHint}`])}.`;
  }
  if (nextPlayer) line += ` Up next to the board is ${nextPlayer}.`;
  speak(line);
}

export function announceTurn(player, score, checkoutHint) {
  let line = pick([
    `${player} to throw. ${score} remaining.`,
    `${player}, you require ${score}.`,
    `Up next, ${player}. ${score} left.`,
  ]);
  if (checkoutHint) line += ` Checkout ${checkoutHint}.`;
  speak(line);
}

export function announceShanghaiVisit({ player, thrown, total, halved, shanghai, gameOver, winners, nextPlayer, target }) {
  if (shanghai) {
    playCue('shanghai');
    speak(`Shanghai! ${player} on ${target || 'the board'}!`);
    return;
  }
  if (gameOver) {
    playCue('win');
    const endLine = (winners || []).length > 1
      ? `${(winners || []).join(' and ')} tie with ${total}`
      : `${(winners || [])[0] || player} wins Shanghai with ${total}`;
    speak(endLine);
    return;
  }
  if (halved) {
    playCue('bust');
    speak(pick([
      `${player} halved to ${total}.`,
      `No score — ${player} halved, now ${total}.`,
      `${player} misses the lot. Halved to ${total}.`,
    ]) + (nextPlayer ? ` Up next to the board is ${nextPlayer}.` : ''));
    return;
  }
  if (thrown > 0) {
    if (thrown >= 60) playCue('ton');
    const body = pick([
      `${thrown}. ${player} now ${total}.`,
      `${thrown} for ${player}. Sitting on ${total}.`,
      `${player}, ${thrown}. Total ${total}.`,
    ]);
    speak(body + (nextPlayer ? ` Up next to the board is ${nextPlayer}.` : ''));
  } else {
    speak(pick([
      `${player} ${total}.`,
      `${player}, no score. Still ${total}.`,
    ]) + (nextPlayer ? ` Up next to the board is ${nextPlayer}.` : ''));
  }
}

const GOLF_LABELS = {
  [-3]: 'Albatross',
  [-2]: 'Eagle',
  [-1]: 'Birdie',
  0: 'Par',
  1: 'Bogey',
  2: 'Double bogey',
  3: 'Triple bogey',
  4: 'Quadruple bogey',
};

export function announceGolfHole({ player, relativeScore, hole, par, nextPlayer, totalLabel }) {
  const label = GOLF_LABELS[relativeScore] || `score ${relativeScore}`;
  playCue(relativeScore <= -1 ? 'golf' : relativeScore >= 2 ? 'bust' : 'tick');
  let line = `${player}, hole ${hole}${par != null ? `, par ${par}` : ''}: ${label}.`;
  if (totalLabel) line += ` Standing ${totalLabel}.`;
  if (nextPlayer) line += ` On the tee, ${nextPlayer}.`;
  speak(line);
}

export function announceVisitSummary(player, darts, total) {
  const labels = (darts || []).filter(Boolean).map((d) => d.label || d).join(', ');
  if (!labels) return;
  speak(`${player}: ${labels}. Visit ${total}.`);
}

/** Preview pack for Announcer settings → Test all. */
export function testAllAnnouncer(prefsOverride) {
  playCue('oneeighty', prefsOverride);
  speakQueue([
    'One hundred and eighty!',
    'One hundred and forty. Requires one hundred and twenty.',
    'Bust. Up next, the next player.',
    'Game shot, and the match!',
    'Birdie. On the tee, the next player.',
    'Shanghai! On nineteen!',
    'Requires forty. Double twenty.',
    'Astra announcer ready for mates night.',
  ], prefsOverride, 1100);
}
